import { createContext, useContext, ReactNode } from 'react';
import { useAuthActions } from '@convex-dev/auth/react';
import { useConvexAuth } from 'convex/react';
import { useConvexAvailable } from './ConvexAvailableContext';

type AuthActions = ReturnType<typeof useAuthActions>;

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: AuthActions['signIn'];
  signOut: AuthActions['signOut'];
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Inner provider that uses Convex Auth hooks.
 * Only rendered when ConvexAuthProvider is available.
 */
function AuthProviderInner({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const actions = useAuthActions();

  const value: AuthContextValue | null =
    actions != null
      ? {
          isAuthenticated,
          isLoading,
          signIn: actions.signIn,
          signOut: actions.signOut,
        }
      : null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Provides auth state and actions when Convex Auth is configured.
 * When Convex is not available, provides null (no auth).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const convexAvailable = useConvexAvailable();

  if (!convexAvailable) {
    return <AuthContext.Provider value={null}>{children}</AuthContext.Provider>;
  }

  return <AuthProviderInner>{children}</AuthProviderInner>;
}

export function useAuthContext(): AuthContextValue | null {
  return useContext(AuthContext);
}
